import React, {Suspense} from 'react';
import {Alert, Box, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useParams} from 'react-router';
import {useSession} from '../../firebase/UserProvider';
import LeftPanelTabs from '../products/components/common/LeftPanelTabs';
import EntityInfoDrawer from './components/EntityInfoDrawer';
import WhiteboardHeader from './components/WhiteboardHeader';
import WhiteboardSearchTab from './components/WhiteboardSearchTab';
import WhiteboardEntitiesTab from './components/WhiteboardEntitiesTab';
import WhiteboardLinksTab from './components/WhiteboardLinksTab';
import ChartFacetChipBar from './components/ChartFacetChipBar';
import ChartAuthorsBar from './components/ChartAuthorsBar';
import {commitVisibleAuthorIds, useChartAuthorProfiles} from './hooks/useChartAuthorProfiles';
import {useTldrawToolbarClearance} from './hooks/useTldrawToolbarClearance';
import {setChartSelectedAuthorIds} from './utils/chartAuthorHighlightAtom';
import {WHITEBOARD_SIDE_PANEL_WIDTH} from './constants/whiteboardPage.constants';
import useResizablePanelWidth from '../../hooks/useResizablePanelWidth';
import {
    ENTITY_CARD_OPEN_DETAILS_EVENT,
    ENTITY_CARD_SHAPE_TYPE,
} from './tldraw/customShapes/entityCardConstants';
import {collectEntityCardShapeUpdates} from './hooks/useEntityCardAvatarSync';
import {useChartEntityProperties} from './hooks/useChartEntityProperties';
import {useChartEntitySummaries} from './hooks/useChartEntitySummaries';
import {useWhiteboardMetadata} from './hooks/useWhiteboardMetadata';
import {preloadWhiteboardEditor} from './preloadWhiteboardEditor';
import {warmupWhiteboardRoom} from './warmupWhiteboardRoom';
import {
    getVisibleEntityIdsKey,
    isEntityFilterActive,
    visibleEntityIdsFromKey,
} from './utils/evaluateEntityPropertyFilters';
import {DEFAULT_MAX_DISTANCE} from './utils/expandEntityIdsByChartDistance';
import {normalizeWhiteboardLinks} from './utils/whiteboardLinks';
import {clearWhiteboardOpenMenus, restoreWhiteboardEditorFocus} from './utils/restoreWhiteboardEditorFocus';
import useAiWidgetStore from '../../stores/useAiWidgetStore';

const PANEL_MIN_WIDTH = 260;
const PANEL_MAX_WIDTH = 760;
const COLLAPSED_STRIP_WIDTH = 40;
const TOGGLE_SIZE = 24;

const SIDEBAR_TABS = [
    {value: 'search', label: 'Search'},
    {value: 'entities', label: 'Entities'},
    {value: 'links', label: 'Links'},
];

function VerticalRailTab({label, count, isActive, onClick}) {
    const hasCount = typeof count === 'number' && count > 0;
    return (
        <Stack
            component="button"
            onClick={onClick}
            aria-label={hasCount ? `${label} (${count})` : label}
            aria-pressed={isActive}
            alignItems="center"
            sx={(theme) => ({
                all: 'unset',
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '3px',
                width: '100%',
                px: 0.5,
                py: 1,
                my: 0.5,
                borderRadius: 1,
                color: isActive
                    ? theme.palette.primary.contrastText
                    : theme.palette.text.secondary,
                bgcolor: isActive
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, isActive ? 1 : 0.3)}`,
                '&:hover': {
                    bgcolor: isActive
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.22),
                },
                transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
                userSelect: 'none',
            })}
        >
            {/* Label rotated vertically */}
            <Box
                component="span"
                sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.72rem',
                    letterSpacing: '0.04em',
                    lineHeight: 1,
                }}
            >
                {label}
            </Box>
            {/* Count badge — only when > 0 */}
            {hasCount && (
                <Box
                    component="span"
                    sx={(theme) => ({
                        minWidth: 16,
                        height: 16,
                        px: '3px',
                        borderRadius: '8px',
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        lineHeight: '16px',
                        textAlign: 'center',
                        color: isActive
                            ? theme.palette.primary.main
                            : theme.palette.primary.contrastText,
                        bgcolor: isActive
                            ? theme.palette.primary.contrastText
                            : theme.palette.primary.main,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    })}
                >
                    {count > 99 ? '99+' : count}
                </Box>
            )}
        </Stack>
    );
}

const WhiteboardEditorWorkspace = React.lazy(() => import('./WhiteboardEditorWorkspace'));

export default function WhiteboardPage() {
    const {whiteboardId} = useParams();
    const {user} = useSession();
    const [activeTab, setActiveTab] = React.useState('search');
    const [collapsed, setCollapsed] = React.useState(false);
    const {width: panelWidth, isResizing, onResizeStart} = useResizablePanelWidth({
        storageKey: 'whiteboard.leftPanelWidth',
        defaultWidth: WHITEBOARD_SIDE_PANEL_WIDTH,
        min: PANEL_MIN_WIDTH,
        max: PANEL_MAX_WIDTH,
    });
    const [drawerEntityWikidataId, setDrawerEntityWikidataId] = React.useState('');
    const [drawerShapeId, setDrawerShapeId] = React.useState(null);
    const [drawerOpenedFromPanel, setDrawerOpenedFromPanel] = React.useState(false);
    const editorRef = React.useRef(null);
    const [syncState, setSyncState] = React.useState({
        ready: false,
        status: 'loading',
        syncError: null,
    });
    const [dragApi, setDragApi] = React.useState({
        addEntityToViewportCenter: null,
    });
    const [chartEntities, setChartEntities] = React.useState([]);
    const [chartConnections, setChartConnections] = React.useState([]);
    const [entityFilters, setEntityFilters] = React.useState([]);
    const [maxDistance, setMaxDistance] = React.useState(DEFAULT_MAX_DISTANCE);
    const [visibleAuthorIds, setVisibleAuthorIds] = React.useState([]);
    const [selectedAuthorIds, setSelectedAuthorIds] = React.useState([]);
    const chartAuthors = useChartAuthorProfiles(visibleAuthorIds);
    const peopleMenuRef = React.useRef(null);
    const stylePanelRef = React.useRef(null);
    const canvasRef = React.useRef(null);
    const panelResizeEndTick = useAiWidgetStore((s) => s.panelResizeEndTick);
    const tickPanelResizeEnd = useAiWidgetStore((s) => s.tickPanelResizeEnd);
    const toolbarClearance = useTldrawToolbarClearance(
        canvasRef,
        `${syncState.ready}:${panelResizeEndTick}`,
    );

    // Same contract as CommunicationHub: when left chrome changes size, tick so
    // WhiteboardEditorWorkspace zoom-to-fits into the new canvas area.
    const prevCollapsedRef = React.useRef(collapsed);
    React.useEffect(() => {
        if (prevCollapsedRef.current === collapsed) return;
        prevCollapsedRef.current = collapsed;
        tickPanelResizeEnd();
    }, [collapsed, tickPanelResizeEnd]);

    const prevResizingRef = React.useRef(isResizing);
    React.useEffect(() => {
        if (prevResizingRef.current && !isResizing) {
            tickPanelResizeEnd();
        }
        prevResizingRef.current = isResizing;
    }, [isResizing, tickPanelResizeEnd]);

    const {
        whiteboard,
        metadataError,
        metadataLoaded,
        refetchTitle,
        refetchLinks,
    } = useWhiteboardMetadata(whiteboardId);

    const entityFiltersActive = React.useMemo(
        () => isEntityFilterActive(entityFilters),
        [entityFilters],
    );

    // Chip bar is always on chart chrome, so load facets whenever cards exist —
    // not only while the Entities-tab advanced editor is open.
    const shouldLoadEntityProperties = chartEntities.length > 0
        || activeTab === 'entities'
        || entityFiltersActive;
    const shouldLoadEntitySummaries = chartEntities.length > 0
        || activeTab === 'entities'
        || entityFiltersActive;

    const {propertiesByEntityId, propertyOptions} = useChartEntityProperties(chartEntities, {
        enabled: shouldLoadEntityProperties,
    });

    const {summariesById: summariesByEntityId, loading: summariesLoading} = useChartEntitySummaries(chartEntities, {
        enabled: shouldLoadEntitySummaries,
    });

    // Content-stable ids so getShapeVisibility identity does not churn on every
    // chartEntities / properties refresh (Tldraw remounts the Editor when that
    // callback changes, which previously caused an endless remount/spinner loop).
    const visibleEntityIdsKey = React.useMemo(
        () => getVisibleEntityIdsKey(
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            entityFilters,
            chartConnections,
            maxDistance,
            {summariesLoading},
        ),
        [
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            entityFilters,
            chartConnections,
            maxDistance,
            summariesLoading,
        ],
    );

    const visibleEntityIds = React.useMemo(
        () => visibleEntityIdsFromKey(visibleEntityIdsKey),
        [visibleEntityIdsKey],
    );

    const links = React.useMemo(
        () => normalizeWhiteboardLinks(whiteboard),
        [whiteboard],
    );

    React.useEffect(() => {
        preloadWhiteboardEditor();
    }, []);

    // Room init (snapshot GET + TLSocketRoom) currently waits until the lazy
    // editor chunk mounts useSync. Start that socket now so empty charts are
    // not blocked on chunk + sync stacked sequentially.
    React.useEffect(() => {
        if (!whiteboardId || !user?.uid) return undefined;
        return warmupWhiteboardRoom(whiteboardId, user.uid);
    }, [whiteboardId, user?.uid]);

    const handleSyncStateChange = React.useCallback((next) => {
        setSyncState(next);
    }, []);

    const handleDragHandlersReady = React.useCallback((api) => {
        setDragApi(api);
    }, []);

    const handleChartEntitiesChange = React.useCallback((nextChartEntities) => {
        setChartEntities(nextChartEntities);
    }, []);

    const handleChartConnectionsChange = React.useCallback((nextChartConnections) => {
        setChartConnections(nextChartConnections);
    }, []);

    const handleVisibleAuthorIdsChange = React.useCallback((nextIds) => {
        setVisibleAuthorIds((prev) => commitVisibleAuthorIds(prev, nextIds));
    }, []);

    const handleAuthorClick = React.useCallback((authorId) => {
        setSelectedAuthorIds((prev) => {
            const next = prev.includes(authorId)
                ? prev.filter((id) => id !== authorId)
                : [...prev, authorId];
            setChartSelectedAuthorIds(next);
            return next;
        });
    }, []);

    React.useEffect(() => {
        setSelectedAuthorIds((prev) => {
            const next = prev.filter((id) => visibleAuthorIds.includes(id));
            if (next.length === prev.length) return prev;
            setChartSelectedAuthorIds(next);
            return next;
        });
    }, [visibleAuthorIds]);

    const closeDrawer = React.useCallback(() => {
        setDrawerEntityWikidataId('');
        setDrawerShapeId(null);
        setDrawerOpenedFromPanel(false);
    }, []);

    const handleDrawerExited = React.useCallback(() => {
        restoreWhiteboardEditorFocus(editorRef.current);
    }, []);

    const handleSelectionChange = React.useCallback((selectedEntityCards) => {
        if (!selectedEntityCards.length) {
            closeDrawer();
            return;
        }

        setDrawerEntityWikidataId((currentWikidataId) => {
            if (!currentWikidataId) return currentWikidataId;

            const currentMatch = selectedEntityCards.find(
                (card) => card.wikidataId === currentWikidataId,
            );
            if (currentMatch) {
                setDrawerShapeId(currentMatch.shapeId);
                return currentWikidataId;
            }

            const next = selectedEntityCards[0];
            setDrawerShapeId(next.shapeId);
            return next.wikidataId;
        });
    }, [closeDrawer]);

    React.useEffect(() => {
        const handleEntityOpenDetails = (event) => {
            const nextWikidataId = event?.detail?.wikidataId;
            if (!nextWikidataId) return;
            // Keep the AI assistant open when an entity chip inside it opens the drawer.
            if (event?.detail?.openedFrom !== 'ai-assistant') {
                useAiWidgetStore.getState().setOpen(false);
            }
            clearWhiteboardOpenMenus(editorRef.current);
            setDrawerEntityWikidataId(nextWikidataId);
            setDrawerShapeId(event?.detail?.shapeId || null);
            setDrawerOpenedFromPanel(event?.detail?.openedFrom === 'panel');
        };

        window.addEventListener(ENTITY_CARD_OPEN_DETAILS_EVENT, handleEntityOpenDetails);
        return () => {
            window.removeEventListener(ENTITY_CARD_OPEN_DETAILS_EVENT, handleEntityOpenDetails);
        };
    }, []);

    const getEntityCardProps = React.useCallback(() => {
        const editor = editorRef.current;
        if (!editor) return {};
        if (drawerShapeId) {
            const shape = editor.getShape?.(drawerShapeId);
            if (shape?.type === ENTITY_CARD_SHAPE_TYPE) {
                return shape.props || {};
            }
        }
        if (!drawerEntityWikidataId) return {};
        const match = (editor.getCurrentPageShapes?.() || []).find((shape) => (
            shape?.type === ENTITY_CARD_SHAPE_TYPE
            && shape.props?.wikidataId === drawerEntityWikidataId
        ));
        return match?.props || {};
    }, [drawerShapeId, drawerEntityWikidataId]);

    const handleUpdateEntityCardProps = React.useCallback((partialProps) => {
        const editor = editorRef.current;
        if (!editor || !partialProps) return;

        const updates = collectEntityCardShapeUpdates(editor, partialProps, {
            wikidataId: drawerEntityWikidataId,
            shapeId: drawerShapeId,
        });
        if (!updates.length) return;

        editor.updateShapes?.(updates);
    }, [drawerEntityWikidataId, drawerShapeId]);

    const drawerEntityShapeCount = React.useMemo(() => {
        if (!drawerEntityWikidataId) return 0;
        const match = chartEntities.find((entity) => entity.wikidataId === drawerEntityWikidataId);
        return match?.shapeCount || 0;
    }, [chartEntities, drawerEntityWikidataId]);

    const title = metadataLoaded ? (whiteboard?.title ?? 'Untitled') : '';

    const tabs = React.useMemo(() => ([
        {
            value: 'search',
            label: 'Search',
            content: (
                <WhiteboardSearchTab
                    onAddEntity={dragApi.addEntityToViewportCenter || (() => {})}
                />
            ),
        },
        {
            value: 'entities',
            label: `Entities (${chartEntities.length})`,
            content: (
                <WhiteboardEntitiesTab
                    chartEntities={chartEntities}
                    entityFilters={entityFilters}
                    onEntityFiltersChange={setEntityFilters}
                    propertyOptions={propertyOptions}
                    propertiesByEntityId={propertiesByEntityId}
                    summariesByEntityId={summariesByEntityId}
                    summariesLoading={summariesLoading}
                    visibleEntityIds={visibleEntityIds}
                />
            ),
        },
        {
            value: 'links',
            label: `Links (${links.length})`,
            content: (
                <WhiteboardLinksTab
                    whiteboardId={whiteboardId}
                    whiteboardTitle={title}
                    links={links}
                    user={user}
                    active={activeTab === 'links'}
                    onLinksChanged={refetchLinks}
                />
            ),
        },
    ]), [
        chartEntities,
        dragApi.addEntityToViewportCenter,
        whiteboardId,
        title,
        links,
        user,
        refetchLinks,
        entityFilters,
        maxDistance,
        propertyOptions,
        propertiesByEntityId,
        summariesByEntityId,
        summariesLoading,
        visibleEntityIds,
        activeTab,
    ]);

    const showLoading = !syncState.ready && syncState.status !== 'error';

    const [filterPanelOpen, setFilterPanelOpen] = React.useState(true);

    const facetChipBarProps = {
        entityFilters,
        onEntityFiltersChange: setEntityFilters,
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        propertyOptions,
        summariesLoading,
        maxDistance,
        onMaxDistanceChange: setMaxDistance,
        attributesOpen: filterPanelOpen,
        onAttributesOpenChange: setFilterPanelOpen,
    };

    return (
        <Stack height="100%" minHeight={0} overflow="hidden">
            <WhiteboardHeader
                whiteboardId={whiteboardId}
                title={title}
                showLoading={showLoading}
                onTitleSaved={refetchTitle}
                peopleMenuRef={peopleMenuRef}
            />

            <Divider/>

            <Stack direction="row" flex={1} minHeight={0} overflow="hidden">
                {/* ── Collapsible / resizable left sidebar ── */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexShrink: 0,
                        overflow: 'visible',
                        width: collapsed ? COLLAPSED_STRIP_WIDTH : panelWidth,
                        transition: isResizing ? 'none' : 'width 0.2s ease',
                    }}
                >
                    {/* Collapsed vertical rail — shown only when collapsed */}
                    {collapsed && (
                        <Stack
                            sx={(theme) => ({
                                width: COLLAPSED_STRIP_WIDTH,
                                flexShrink: 0,
                                bgcolor: 'background.paper',
                                borderRight: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: 5,
                                pb: 1,
                                px: 0.5,
                                gap: 0,
                            })}
                        >
                            {SIDEBAR_TABS.map((tab) => (
                                <VerticalRailTab
                                    key={tab.value}
                                    label={tab.label}
                                    count={
                                        tab.value === 'entities' ? chartEntities.length
                                        : tab.value === 'links' ? links.length
                                        : undefined
                                    }
                                    isActive={activeTab === tab.value}
                                    onClick={() => {
                                        setActiveTab(tab.value);
                                        setCollapsed(false);
                                    }}
                                />
                            ))}
                        </Stack>
                    )}

                    {/* Full panel content — hidden when collapsed */}
                    <Stack
                        sx={{
                            width: collapsed ? 0 : panelWidth,
                            overflow: 'hidden',
                            transition: isResizing ? 'none' : 'width 0.2s ease',
                            flexShrink: 0,
                            bgcolor: 'background.paper',
                            minHeight: 0,
                            '& > [role="tabpanel"]': {
                                flex: 1,
                                minHeight: 0,
                            },
                        }}
                    >
                        <LeftPanelTabs
                            tabs={tabs}
                            activeTab={activeTab}
                            onChange={(_event, value) => setActiveTab(value)}
                            ariaLabel="Whiteboard left panel tabs"
                        />
                    </Stack>

                    {/* Drag-to-resize handle — hidden while collapsed */}
                    {!collapsed && (
                        <Box
                            role="separator"
                            aria-orientation="vertical"
                            aria-label="Resize whiteboard left panel"
                            onPointerDown={onResizeStart}
                            sx={(theme) => ({
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                right: -3,
                                width: 6,
                                zIndex: 15,
                                cursor: 'col-resize',
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.25),
                                },
                                ...(isResizing && {
                                    bgcolor: alpha(theme.palette.primary.main, 0.4),
                                }),
                            })}
                        />
                    )}

                    {/* Collapse / expand toggle */}
                    <Tooltip
                        title={collapsed ? 'Expand panel' : 'Collapse panel'}
                        placement="right"
                        arrow
                    >
                        <IconButton
                            size="small"
                            onClick={() => setCollapsed((c) => !c)}
                            aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
                            sx={(theme) => ({
                                position: 'absolute',
                                top: collapsed ? 8 : 52,
                                zIndex: 20,
                                width: TOGGLE_SIZE,
                                height: TOGGLE_SIZE,
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '50%',
                                boxShadow: theme.shadows[2],
                                transition: 'top 0.2s ease',
                                ...(collapsed
                                    ? {
                                        left: (COLLAPSED_STRIP_WIDTH - TOGGLE_SIZE) / 2,
                                        right: 'auto',
                                    }
                                    : {
                                        left: 'auto',
                                        right: -TOGGLE_SIZE / 2,
                                    }),
                                '&:hover': {bgcolor: 'action.hover'},
                                '& .MuiSvgIcon-root': {fontSize: '1rem'},
                            })}
                        >
                            {collapsed ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider orientation="vertical"/>

                <Box
                    ref={canvasRef}
                    data-chart-canvas=""
                    flex={1}
                    minWidth={0}
                    minHeight={0}
                    sx={{position: 'relative'}}
                >
                    {metadataError && (
                        <Alert severity="warning" sx={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10}}>
                            Failed to load whiteboard metadata.
                        </Alert>
                    )}

                    {/* Full-width toolbar background strip — unifies tldraw pill + REACH slider into one bar */}
                    <Box
                        data-chart-fit-obstruction=""
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 40,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            pointerEvents: 'none',
                        })}
                    />

                    {/* Toolbar filter — fills the row to the right of tldraw tools so AUTHORS can shrink */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: toolbarClearance,
                            right: 8,
                            height: 40,
                            zIndex: 500,
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                            overflow: 'hidden',
                            minWidth: 0,
                        }}
                    >
                        <Box sx={{
                            pointerEvents: 'auto',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            minWidth: 0,
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                        }}
                        >
                            <ChartAuthorsBar
                                authors={chartAuthors}
                                selectedAuthorIds={selectedAuthorIds}
                                onAuthorClick={handleAuthorClick}
                            />
                            <ChartFacetChipBar {...facetChipBarProps} toolbarOnly/>
                        </Box>
                    </Box>

                    {/* Attribute chips — floats over canvas, stuck below the tldraw top toolbar.
                        data-chart-fit-obstruction tells zoom-to-fit to exclude this height. */}
                    <Box
                        data-chart-fit-obstruction=""
                        sx={{position: 'absolute', top: 40, left: 0, right: 0, zIndex: 400, pointerEvents: 'none'}}
                    >
                        <Box sx={{pointerEvents: 'auto'}}>
                            <ChartFacetChipBar {...facetChipBarProps}/>
                        </Box>
                        {/* Style panel slot — portalled in from WhiteboardStylePanel (inside tldraw tree) */}
                        <Box
                            ref={stylePanelRef}
                            sx={{
                                position: 'absolute',
                                top: 'calc(100% + 5px)',
                                right: 8,
                                zIndex: 10,
                                pointerEvents: 'auto',
                            }}
                        />
                    </Box>

                    <Suspense
                        fallback={(
                            <Stack
                                flex={1}
                                alignItems="center"
                                justifyContent="center"
                                spacing={2}
                                sx={{color: 'text.secondary', height: '100%'}}
                            >
                                <CircularProgress size={32}/>
                                <Typography variant="body2" align="center" maxWidth={360}>
                                    Loading whiteboard editor…
                                </Typography>
                            </Stack>
                        )}
                    >
                        <WhiteboardEditorWorkspace
                            whiteboardId={whiteboardId}
                            user={user}
                            editorRef={editorRef}
                            onSelectionChange={handleSelectionChange}
                            onChartEntitiesChange={handleChartEntitiesChange}
                            onChartConnectionsChange={handleChartConnectionsChange}
                            onSyncStateChange={handleSyncStateChange}
                            onDragHandlersReady={handleDragHandlersReady}
                            entityFiltersActive={entityFiltersActive}
                            visibleEntityIdsKey={visibleEntityIdsKey}
                            onSetEntityFilters={setEntityFilters}
                            peopleMenuRef={peopleMenuRef}
                            stylePanelRef={stylePanelRef}
                            onVisibleAuthorIdsChange={handleVisibleAuthorIdsChange}
                        />
                    </Suspense>
                </Box>
            </Stack>

            <EntityInfoDrawer
                open={Boolean(drawerEntityWikidataId)}
                wikidataId={drawerEntityWikidataId}
                shapeId={drawerShapeId}
                openedFromPanel={drawerOpenedFromPanel}
                entityShapeCount={drawerEntityShapeCount}
                currentChartId={whiteboardId}
                onClose={closeDrawer}
                onExited={handleDrawerExited}
                getEntityCardProps={getEntityCardProps}
                onUpdateEntityCardProps={handleUpdateEntityCardProps}
            />
        </Stack>
    );
}
